import { AxiosError } from "axios";
import { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { errorShouldBeRetried } from "@/components/Portal/UserAccount/Security/utils";
import {
  stepDescriptionClassName,
  stepHeadingClassName,
} from "@/components/Welcome/AuthenticatorSetup/authenticator.utils";
import { NameConfirmationField } from "@/components/Welcome/AuthenticatorSetup/NameConfirmationField";
import { useAlert } from "@/hooks/useAlert";
import { useCancelTwoFactorSetup } from "@/hooks/useCancelTwoFactorSetup";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useChangeTwoFactorAuthentication } from "@/services/auth";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

type ConfirmStandardForm = {
  name: string;
};
export const TwoFactorConfirmStandard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmStandardForm>({
    defaultValues: {
      name: "",
    },
    mode: "onChange",
  });
  const name = useWatch({ control, name: "name" });

  const { mutateAsync: cancelSetup, isLoading: isCancelingSetup } =
    useCancelTwoFactorSetup();
  const { mutateAsync: changeTwoFactorAuth } =
    useChangeTwoFactorAuthentication();
  const showToast = useAlert();

  const isSubmitting = useRef(false);

  const onSubmit = () => {
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;

    const handleSuccess = () => {
      showToast({
        text: t("portal:user-account.security.2fa.confirmStandard.success"),
        type: AlertTypes.success,
      });
      navigate(portalRoutes.cockpit.base);
    };

    const tryToChange = () =>
      changeTwoFactorAuth({
        params: { method: ChangeTwoFactorAuthenticationMethod.STANDARD },
      });

    tryToChange()
      .then(handleSuccess)
      .catch((error: AxiosError<any>) => {
        if (errorShouldBeRetried(error)) {
          cancelSetup().then(() => {
            setTimeout(() => tryToChange().then(handleSuccess), 5000);
          });
        } else {
          showToast({
            text: t("portal:user-account.security.2fa.confirmStandard.error"),
            type: AlertTypes.error,
          });
        }
      })
      .finally(() => {
        isSubmitting.current = false;
      });
  };

  return (
    <>
      {isCancelingSetup && <LoadingSpinner />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("portal:user-account.security.buttons.cancel")}
              </Button>
              <SubmitButton
                fullWidth={false}
                size="sm"
                disabled={isCancelingSetup || !name.trim()}
                label={t("common.buttons.confirm")}
              />
            </>
          }
        >
          <h2 className={stepHeadingClassName}>
            {t("portal:user-account.security.2fa.confirmStandard.subtitle")}
          </h2>
          <p className={stepDescriptionClassName}>
            {t("portal:user-account.security.2fa.confirmStandard.text1")}
          </p>
          <p className={stepDescriptionClassName}>
            {t("portal:user-account.security.2fa.confirmStandard.text2")}
          </p>
          <p className={stepDescriptionClassName}>
            {t("portal:user-account.security.2fa.confirmStandard.text3")}
          </p>
          <NameConfirmationField
            register={register}
            errors={errors}
            title={t(
              "portal:user-account.security.2fa.confirmStandard.steps.2.title",
            )}
          />
        </ProfileCard>
      </form>
    </>
  );
};
